// All API endpoint URLs as constants

const ENDPOINTS = {
  // Authentication Endpoints
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH_TOKEN: '/auth/refresh-token',
    ME: '/auth/me',
    VERIFY_AGE: '/auth/verify-age',
    FORGOT_PASSWORD: '/auth/forgot/password',
    VERIFY_OTP: '/auth/verify/otp',
    RESET_PASSWORD: '/auth/reset/password',
    CHANGE_PASSWORD: (uuid) => `/auth/change/password/${uuid}`,
    EXPORT: '/auth/export',
    UPLOAD_FILE: '/auth/upload/file',
    UPLOAD_MULTIPLE_FILES: '/auth/upload/multiple/files',
    UPLOAD_DOCUMENT: '/auth/upload/document',
  },

  // Products Endpoints
  PRODUCTS: {
    BASE: '/products',
    BY_ID: (id) => `/products/${id}`,
    SEARCH: '/products/search',
    FILTER: '/products/filter',
    CATEGORIES: '/products/categories',
    FEATURED: '/products/featured',
  },
  
  // Product (Admin) Endpoints
  PRODUCT: {
    LIST: '/product/list',
    USER_LIST: '/product/user/list',
    VIEW: (uuid) => `/product/view/${uuid}`,
    CREATE: '/product/create',
    GENERATE_VARIANTS: '/product/generate/variants',
    UPDATE: (uuid) => `/product/update/${uuid}`,
    STATUS_UPDATE: (uuid) => `/product/status/update/${uuid}`,
  },

  // Cart Endpoints
  CART: {
    BASE: '/cart',
    LIST: '/cart/list',
    UPDATE_QUANTITY: (uuid) => `/cart/update/quantity/${uuid}`,
    DELETE: (uuid) => `/cart/delete/${uuid}`,
    UPDATE_USER: '/cart/update/user',
    ITEMS: '/cart/items',
    ITEM_BY_ID: (id) => `/cart/items/${id}`,
    CLEAR: '/cart/clear',
    CREATE: '/cart/create',
  },

  // Checkout Endpoints
  CHECKOUT: {
    CREATE: '/checkout/create',
    GET: '/checkout/get',
    RATES: '/checkout/rates',
    SELECT_RATE: '/checkout/select/rate',
  },

  // Payment Endpoints
  PAYMENT: {
    CREATE: '/payment/create',
    UPDATE: (uuid) => `/payment/update/${uuid}`,
    STATUS_UPDATE: (uuid) => `/payment/status/update/${uuid}`,
    LIST: '/payment/list',
    USER_LIST: '/payment/user/list',
  },

  // Orders Endpoints
  ORDERS: {
    BASE: '/order/create',
    CREATE: '/order/create',
    LIST: '/order/list',
    EXPORT: '/order/export',
    USER_LIST: '/order/user/list',
    VIEW: (uuid) => `/order/view/${uuid}`,
    STATUS_UPDATE: (uuid) => `/order/status/update/${uuid}`,
    VERIFY_PAYMENT: '/order/verify/payment',
    CREATE_PAYMENT: '/order/payment/create',
    TRANSACTION_LIST: '/order/transaction/list',
    TRANSACTION_EXPORT: '/order/transaction/export',
    TRANSACTION_VIEW: (uuid) => `/order/transaction/view/${uuid}`,
  },

  // Shipping Endpoints
  SHIPPING: {
    TRACKING_BY_ORDER: (orderUuid) =>
      `/shipping/tracking/order/${encodeURIComponent(String(orderUuid || '').trim())}`,
    TRACKING: (carrier, trackingNumber) =>
      `/shipping/tracking/${encodeURIComponent(String(carrier || '').trim().toLowerCase())}/${encodeURIComponent(String(trackingNumber || '').trim())}`,
  },

  // Subscriptions Endpoints
  SUBSCRIPTIONS: {
    BASE: '/subscriptions',
    LIST: '/subscription/list',
    EXPORT: '/subscription/export',
    USER_LIST: '/subscription/user/list',
    BY_ID: (id) => `/subscriptions/${id}`,
    USER_SUBSCRIPTIONS: '/subscriptions/user',
    CANCEL: (id) => `/subscriptions/${id}/cancel`,
    PAUSE: (id) => `/subscriptions/${id}/pause`,
    RESUME: (id) => `/subscriptions/${id}/resume`,
    UPDATE: (id) => `/subscriptions/${id}/update`,
  },

  // Stores Endpoints (Admin)
  STORES: {
    CREATE: '/store/create',
    LIST: '/store/list',
    USER_LIST: '/store/user/list',
    UPDATE: (uuid) => `/store/update/${uuid}`,
    VIEW: (uuid) => `/store/view/${uuid}`,
    STATUS_UPDATE: (uuid) => `/store/status/update/${uuid}`,
    UPLOAD: '/store/bulk/upload',
  },

  // Lab Reports Endpoints (Admin)
  LABS: {
    BASE: '/labs',
    BY_ID: (id) => `/labs/${id}`,
    DOWNLOAD: (id) => `/lab/download/${id}`,
    UPLOAD: '/labs/upload',
    CREATE: '/lab/create',
    LIST: '/lab/list',
    USER_LIST: '/lab/user/list',
    UPDATE: (uuid) => `/lab/update/${uuid}`,
    VIEW: (uuid) => `lab/view/${uuid}`,
    STATUS_UPDATE: (uuid) => `/lab/status/update/${uuid}`,
  },

  // Attribute Type Endpoints
  ATTRIBUTES: {
    TYPE: {
      CREATE: '/attribute/type/create',
      LIST: '/attribute/type/list',
      UPDATE: (uuid) => `/attribute/type/update/${uuid}`,
      STATUS_UPDATE: (uuid) => `/attribute/type/status/update/${uuid}`,
    },
    VALUE: {
      CREATE: '/attribute/value/create',
      LIST: '/attribute/value/list',
      UPDATE: (uuid) => `/attribute/value/update/${uuid}`,
      STATUS_UPDATE: (uuid) => `/attribute/value/status/update/${uuid}`,
    },
  },

  // Categories Endpoints
  CATEGORIES: {
    CREATE: '/category/create',
    LIST: '/category/list',
    USER_LIST: '/category/user/list',
    UPDATE: (uuid) => `/category/update/${uuid}`,
    VIEW: (uuid) => `/category/view/${uuid}`,
    STATUS_UPDATE: (uuid) => `/category/status/update/${uuid}`,
    DROPDOWN: '/category/dropdown',
  },

  // Contact / Support Endpoints
  CONTACT: {
    CREATE: '/contact/create',
    LIST: '/contact/list',
  },

  // Subcategory Endpoints
  SUBCATEGORY: {
    CREATE: '/subcategory/create',
    UPDATE: (uuid) => `/subcategory/update/${uuid}`,
    DROPDOWN: '/subcategory/dropdown',
  },

  // User Endpoints
  USER: {
    PROFILE: (uuid) => `/auth/profile/view/${uuid}`,
    UPDATE_PROFILE: (uuid) => `/auth/update/${uuid}`,
    ADDRESSES: '/user/addresses',
    ADDRESS_LIST: '/user/address/list',
    ADDRESS_CREATE: '/user/address/create',
    ADDRESS_UPDATE: (uuid) => `/user/address/update/${uuid}`,
    ADDRESS_DELETE: (uuid) => `/user/address/delete/${uuid}`,
    ADDRESS_BY_ID: (id) => `/user/addresses/${id}`,
    PREFERENCES: '/user/preferences',
    ORDER_HISTORY: '/user/orders',
  },

  // Admin Endpoints
  ADMIN: {
    DASHBOARD: '/auth/admin/dashboard',
    USERS: '/admin/users',
    USER_BY_ID: (id) => `/admin/users/${id}`,
    USER_STATUS: (id) => `/admin/users/${id}/status`,
  },
};

export default ENDPOINTS;
